import type { ProblemType, LeafType, MixType, ProblemDefinition, ProblemGroup, ProblemGroupId } from '../types';

// 学習第一の順序: まず計算の土台（整数の桁 → 小数の桁 → 分数）を固め、
// そのあと文章題（特殊算）を直感的なもの順に（植木 → テープ → 和差 → 分配）。
// 各グループ内は やさしい → むずかしい、最後に「まとめ」。
export const STAGE_ORDER: ProblemType[] = [
  // 1. 整数の桁（いちばんやさしい・数の感覚づくり）
  'KETA_X10',
  'KETA_D10',
  'KETA_X100',
  'KETA_D100',
  'KETA_DECIMAL',
  // 2. 小数の桁
  'KETA_DEC_X10',
  'KETA_DEC_X100',
  'KETA_DEC_MIX',
  'KETA_MIX',
  // 3. 小数の計算（位の構成・たし算ひき算）
  'DEC_COMPOSE',
  'DEC_ADD',
  'DEC_SUB',
  'DEC_ADDSUB',
  'DEC_MIX',
  // 4. たんい（単位の換算）— ×10/×100/×1000 の応用
  'UNIT_LENGTH',
  'UNIT_VOLUME',
  'UNIT_WEIGHT',
  'UNIT_ADD',
  'UNIT_DECIMAL',
  'UNIT_MIX',
  // 5. がい数（四捨五入）— 位の理解の応用
  'ROUND_NORMAL',
  'ROUND_RANGE',
  'ROUND_WORD',
  'ROUND_MIX',
  // 6. 分数
  'FRAC_REDUCE',
  'FRAC_COMMON',
  'FRAC_MIX',
  // 7. 植木算（絵で考えられる文章題）
  'UEKI_A',
  'UEKI_B',
  'UEKI_C',
  'UEKI_MIX',
  // 8. テープ・リング（植木算の考え方の応用）
  'TAPE_LEN',
  'TAPE_GLUE',
  // 'RING', // むずかしいので今はオフ
  'TAPE_NUM',
  'MASTER_MIX',
  // 9. 和差算（線分図）
  'WASA_FIND_BIG',
  'WASA_FIND_SMALL',
  'WASA_MIX',
  // 10. 分配算（線分図・いちばんむずかしい）
  'BUNPAI_BASIC',
  'BUNPAI_OFFSET',
  'BUNPAI_MIX',
];

// まとめ（MIX）問題が、どのリーフ種別から出題されるか。
// バンクには MIX 用の問題は保存せず、実行時に構成要素から1問選ぶ。
export const MIX_COMPOSITION: Partial<Record<MixType, LeafType[]>> = {
  KETA_DEC_MIX: ['KETA_DEC_X10', 'KETA_DEC_X100'],
  KETA_MIX: [
    'KETA_X10', 'KETA_D10', 'KETA_X100', 'KETA_D100', 'KETA_DECIMAL',
    'KETA_DEC_X10', 'KETA_DEC_X100',
  ],
  DEC_MIX: ['DEC_COMPOSE', 'DEC_ADD', 'DEC_SUB', 'DEC_ADDSUB'],
  UNIT_MIX: ['UNIT_LENGTH', 'UNIT_VOLUME', 'UNIT_WEIGHT', 'UNIT_ADD', 'UNIT_DECIMAL'],
  ROUND_MIX: ['ROUND_NORMAL', 'ROUND_RANGE', 'ROUND_WORD'],
  FRAC_MIX: ['FRAC_REDUCE', 'FRAC_COMMON'],
  UEKI_MIX: ['UEKI_A', 'UEKI_B', 'UEKI_C'],
  MASTER_MIX: ['UEKI_A', 'UEKI_B', 'UEKI_C', 'TAPE_LEN', 'TAPE_GLUE', 'TAPE_NUM'],
  WASA_MIX: ['WASA_FIND_BIG', 'WASA_FIND_SMALL'],
  BUNPAI_MIX: ['BUNPAI_BASIC', 'BUNPAI_OFFSET'],
};

// 実際にバンク(JSON)を持つリーフ種別（MIX を除いたもの）。
export const LEAF_TYPES = STAGE_ORDER.filter(
  (t) => !(t in MIX_COMPOSITION)
) as LeafType[];

export const REQUIRED_WINS: Record<ProblemType, number> = {
  UEKI_A: 3,
  UEKI_B: 3,
  UEKI_C: 3,
  UEKI_MIX: 5,
  TAPE_LEN: 3,
  TAPE_GLUE: 3,
  TAPE_NUM: 3,
  MASTER_MIX: 5,
  // Place Value
  KETA_X10: 3,
  KETA_D10: 3,
  KETA_X100: 3,
  KETA_D100: 3,
  KETA_DECIMAL: 5,
  // Decimal number place value
  KETA_DEC_X10: 3,
  KETA_DEC_X100: 3,
  KETA_DEC_MIX: 5,
  KETA_MIX: 5,
  // Decimal calculation
  DEC_COMPOSE: 3,
  DEC_ADD: 3,
  DEC_SUB: 3,
  DEC_ADDSUB: 3,
  DEC_MIX: 5,
  // Unit conversion
  UNIT_LENGTH: 3,
  UNIT_VOLUME: 3,
  UNIT_WEIGHT: 3,
  UNIT_ADD: 3,
  UNIT_DECIMAL: 3,
  UNIT_MIX: 5,
  // Rounding
  ROUND_NORMAL: 3,
  ROUND_RANGE: 3,
  ROUND_WORD: 3,
  ROUND_MIX: 5,
  // Fractions
  FRAC_REDUCE: 3,
  FRAC_COMMON: 3,
  FRAC_MIX: 5,
  // Wasa-zan
  WASA_FIND_BIG: 3,
  WASA_FIND_SMALL: 3,
  WASA_MIX: 5,
  // Bunpai-zan
  BUNPAI_BASIC: 3,
  BUNPAI_OFFSET: 3,
  BUNPAI_MIX: 5,
};

// Problem Groups
export const PROBLEM_GROUPS: ProblemGroup[] = [
  {
    id: 'UEKI',
    title: '植木算',
    description: '木や旗を等間隔に植える問題',
    color: '#4CAF50',
    icon: '🌳',
    types: ['UEKI_A', 'UEKI_B', 'UEKI_C', 'UEKI_MIX'],
  },
  {
    id: 'TAPE',
    title: 'テープ・リング',
    description: 'テープやリングをつなげる問題',
    color: '#FF9800',
    icon: '📏',
    types: ['TAPE_LEN', 'TAPE_GLUE', /* 'RING', */ 'TAPE_NUM', 'MASTER_MIX'],
  },
  {
    id: 'KETA_INT',
    title: '整数の桁',
    description: '整数の×10、÷10など',
    color: '#3F51B5',
    icon: '🔢',
    types: ['KETA_X10', 'KETA_D10', 'KETA_X100', 'KETA_D100', 'KETA_DECIMAL'],
  },
  {
    id: 'KETA_DEC',
    title: '小数の桁',
    description: '小数の×10、×100など',
    color: '#8BC34A',
    icon: '🔸',
    types: ['KETA_DEC_X10', 'KETA_DEC_X100', 'KETA_DEC_MIX', 'KETA_MIX'],
  },
  {
    id: 'DECIMAL',
    title: '小数の計算',
    description: '小数のたし算・ひき算、位の構成',
    color: '#26C6DA',
    icon: '🧮',
    types: ['DEC_COMPOSE', 'DEC_ADD', 'DEC_SUB', 'DEC_ADDSUB', 'DEC_MIX'],
  },
  {
    id: 'UNIT',
    title: '単位（たんい）',
    description: 'cm・m・km・L・dL・mL・kg・g の計算',
    color: '#5C6BC0',
    icon: '📐',
    types: ['UNIT_LENGTH', 'UNIT_VOLUME', 'UNIT_WEIGHT', 'UNIT_ADD', 'UNIT_DECIMAL', 'UNIT_MIX'],
  },
  {
    id: 'ROUND',
    title: 'がい数（四捨五入）',
    description: '四捨五入してがい数にする',
    color: '#26A69A',
    icon: '≈',
    types: ['ROUND_NORMAL', 'ROUND_RANGE', 'ROUND_WORD', 'ROUND_MIX'],
  },
  {
    id: 'FRACTION',
    title: '分数',
    description: '約分・通分など分数の問題',
    color: '#9C27B0',
    icon: '½',
    types: ['FRAC_REDUCE', 'FRAC_COMMON', 'FRAC_MIX'],
  },
  {
    id: 'WASA',
    title: '和差算',
    description: '和と差から数を求める',
    color: '#FF9800',
    icon: '⚖️',
    types: ['WASA_FIND_BIG', 'WASA_FIND_SMALL', 'WASA_MIX'],
  },
  {
    id: 'BUNPAI',
    title: '分配算',
    description: '比で分ける問題',
    color: '#00BCD4',
    icon: '🍰',
    types: ['BUNPAI_BASIC', 'BUNPAI_OFFSET', 'BUNPAI_MIX'],
  },
];

export const GROUP_ORDER: ProblemGroupId[] = ['KETA_INT', 'KETA_DEC', 'DECIMAL', 'UNIT', 'ROUND', 'FRACTION', 'UEKI', 'TAPE', 'WASA', 'BUNPAI'];

export const PROBLEM_DEFINITIONS: Record<ProblemType, ProblemDefinition> = {
  UEKI_A: {
    id: 'UEKI_A',
    title: 'パターン A',
    description: '植木算：両端に植える',
    color: '#4CAF50',
    icon: 'A',
    group: 'UEKI',
  },
  UEKI_B: {
    id: 'UEKI_B',
    title: 'パターン B',
    description: '植木算：まわりに植える',
    color: '#2196F3',
    icon: 'B',
    group: 'UEKI',
  },
  UEKI_C: {
    id: 'UEKI_C',
    title: 'パターン C',
    description: '植木算：両端には植えない',
    color: '#9C27B0',
    icon: 'C',
    group: 'UEKI',
  },
  UEKI_MIX: {
    id: 'UEKI_MIX',
    title: '植木算まとめ',
    description: '植木算3パターンのテスト',
    color: '#009688',
    icon: '★',
    group: 'UEKI',
  },
  TAPE_LEN: {
    id: 'TAPE_LEN',
    title: 'パターン D',
    description: 'テープ：長さを求める',
    color: '#FF9800',
    icon: 'D',
    group: 'TAPE',
  },
  TAPE_GLUE: {
    id: 'TAPE_GLUE',
    title: 'パターン E',
    description: 'テープ：のりしろを求める',
    color: '#795548',
    icon: 'E',
    group: 'TAPE',
  },
  TAPE_NUM: {
    id: 'TAPE_NUM',
    title: 'パターン G',
    description: 'テープ：枚数を求める(応用)',
    color: '#FF5722',
    icon: 'G',
    group: 'TAPE',
  },
  MASTER_MIX: {
    id: 'MASTER_MIX',
    title: 'マスター ★',
    description: '全種類のランダムテスト',
    color: '#E91E63',
    icon: '♛',
    group: 'TAPE',
  },
  // Place Value (桁上り・桁下がり)
  KETA_X10: {
    id: 'KETA_X10',
    title: '×10',
    description: '10倍にする',
    color: '#3F51B5',
    icon: '×10',
    group: 'KETA_INT',
  },
  KETA_D10: {
    id: 'KETA_D10',
    title: '÷10',
    description: '10で割る（整数）',
    color: '#673AB7',
    icon: '÷10',
    group: 'KETA_INT',
  },
  KETA_X100: {
    id: 'KETA_X100',
    title: '×100,×1000',
    description: '100倍・1000倍',
    color: '#00BCD4',
    icon: '×100',
    group: 'KETA_INT',
  },
  KETA_D100: {
    id: 'KETA_D100',
    title: '÷100,÷1000',
    description: '100・1000で割る',
    color: '#009688',
    icon: '÷100',
    group: 'KETA_INT',
  },
  KETA_DECIMAL: {
    id: 'KETA_DECIMAL',
    title: '小数点',
    description: '小数の答え（0.1など）',
    color: '#FF5722',
    icon: '0.1',
    group: 'KETA_INT',
  },
  // Decimal number place value (小数の桁上がり・桁下がり)
  KETA_DEC_X10: {
    id: 'KETA_DEC_X10',
    title: '小数×10',
    description: '小数を10倍',
    color: '#8BC34A',
    icon: '×10',
    group: 'KETA_DEC',
  },
  KETA_DEC_X100: {
    id: 'KETA_DEC_X100',
    title: '小数×100',
    description: '小数を100倍・1000倍',
    color: '#CDDC39',
    icon: '×100',
    group: 'KETA_DEC',
  },
  KETA_DEC_MIX: {
    id: 'KETA_DEC_MIX',
    title: '小数まとめ ★',
    description: '小数の桁上がり総合',
    color: '#FFC107',
    icon: '小★',
    group: 'KETA_DEC',
  },
  KETA_MIX: {
    id: 'KETA_MIX',
    title: '桁まとめ ★',
    description: '桁上り・桁下がり総合',
    color: '#E91E63',
    icon: '桁★',
    group: 'KETA_DEC',
  },
  // Decimal calculation (小数の計算)
  DEC_COMPOSE: {
    id: 'DEC_COMPOSE',
    title: '小数のしくみ',
    description: '0.1・0.01・0.001 の構成',
    color: '#26C6DA',
    icon: '🔢',
    group: 'DECIMAL',
  },
  DEC_ADD: {
    id: 'DEC_ADD',
    title: '小数のたし算',
    description: '0.58 + 0.76 など',
    color: '#00ACC1',
    icon: '＋',
    group: 'DECIMAL',
  },
  DEC_SUB: {
    id: 'DEC_SUB',
    title: '小数のひき算',
    description: '4.1 − 2.58 など',
    color: '#0097A7',
    icon: '−',
    group: 'DECIMAL',
  },
  DEC_ADDSUB: {
    id: 'DEC_ADDSUB',
    title: '3つの計算・かっこ',
    description: '3項・かっこの小数計算',
    color: '#00838F',
    icon: '（）',
    group: 'DECIMAL',
  },
  DEC_MIX: {
    id: 'DEC_MIX',
    title: '小数の計算まとめ ★',
    description: '小数の計算の総合テスト',
    color: '#006064',
    icon: '小★',
    group: 'DECIMAL',
  },
  // Unit conversion (単位の換算)
  UNIT_LENGTH: {
    id: 'UNIT_LENGTH',
    title: '長さ',
    description: 'cm と m',
    color: '#5C6BC0',
    icon: '📏',
    group: 'UNIT',
  },
  UNIT_VOLUME: {
    id: 'UNIT_VOLUME',
    title: 'かさ',
    description: 'L・dL・mL',
    color: '#42A5F5',
    icon: '🥤',
    group: 'UNIT',
  },
  UNIT_WEIGHT: {
    id: 'UNIT_WEIGHT',
    title: '重さ',
    description: 'kg と g',
    color: '#7E57C2',
    icon: '⚖️',
    group: 'UNIT',
  },
  UNIT_ADD: {
    id: 'UNIT_ADD',
    title: '単位のたし算',
    description: 'ちがう単位を合わせる',
    color: '#3949AB',
    icon: '＋',
    group: 'UNIT',
  },
  UNIT_DECIMAL: {
    id: 'UNIT_DECIMAL',
    title: 'km・小数・複名数',
    description: '6.9km=?m、4270cm=?m など',
    color: '#1A237E',
    icon: 'km',
    group: 'UNIT',
  },
  UNIT_MIX: {
    id: 'UNIT_MIX',
    title: '単位まとめ ★',
    description: '単位の総合テスト',
    color: '#283593',
    icon: '単★',
    group: 'UNIT',
  },
  // Rounding (がい数・四捨五入)
  ROUND_NORMAL: {
    id: 'ROUND_NORMAL',
    title: '四捨五入',
    description: 'がい数にする',
    color: '#26A69A',
    icon: '≈',
    group: 'ROUND',
  },
  ROUND_RANGE: {
    id: 'ROUND_RANGE',
    title: '◯になる数',
    description: '四捨五入して〇になる整数',
    color: '#00897B',
    icon: '範',
    group: 'ROUND',
  },
  ROUND_WORD: {
    id: 'ROUND_WORD',
    title: 'がい数の文章題',
    description: '合計・差を四捨五入',
    color: '#00796B',
    icon: '文',
    group: 'ROUND',
  },
  ROUND_MIX: {
    id: 'ROUND_MIX',
    title: 'がい数まとめ ★',
    description: '四捨五入の総合テスト',
    color: '#00695C',
    icon: '概★',
    group: 'ROUND',
  },
  // Fractions (分数)
  FRAC_REDUCE: {
    id: 'FRAC_REDUCE',
    title: '約分',
    description: '分数を約分する',
    color: '#9C27B0',
    icon: '約',
    group: 'FRACTION',
  },
  FRAC_COMMON: {
    id: 'FRAC_COMMON',
    title: '通分',
    description: '分母をそろえる',
    color: '#7B1FA2',
    icon: '通',
    group: 'FRACTION',
  },
  FRAC_MIX: {
    id: 'FRAC_MIX',
    title: '分数まとめ ★',
    description: '約分・通分の総合テスト',
    color: '#6A1B9A',
    icon: '分★',
    group: 'FRACTION',
  },
  // Wasa-zan (和差算)
  WASA_FIND_BIG: {
    id: 'WASA_FIND_BIG',
    title: '大きいほうは？',
    description: '和と差から大きい数を計算',
    color: '#FF9800',
    icon: '大',
    group: 'WASA',
  },
  WASA_FIND_SMALL: {
    id: 'WASA_FIND_SMALL',
    title: '小さいほうは？',
    description: '和と差から小さい数を計算',
    color: '#FFC107',
    icon: '小',
    group: 'WASA',
  },
  WASA_MIX: {
    id: 'WASA_MIX',
    title: '和差算まとめ ★',
    description: '和差算の総合テスト',
    color: '#E65100',
    icon: '和★',
    group: 'WASA',
  },
  // Bunpai-zan (分配算)
  BUNPAI_BASIC: {
    id: 'BUNPAI_BASIC',
    title: '基本の分配',
    description: '比で分ける基本問題',
    color: '#00BCD4',
    icon: '比',
    group: 'BUNPAI',
  },
  BUNPAI_OFFSET: {
    id: 'BUNPAI_OFFSET',
    title: '差を含む分配',
    description: '「〇より△多い」などの応用',
    color: '#0097A7',
    icon: '差',
    group: 'BUNPAI',
  },
  BUNPAI_MIX: {
    id: 'BUNPAI_MIX',
    title: '分配算まとめ ★',
    description: '分配算の総合テスト',
    color: '#006064',
    icon: '配★',
    group: 'BUNPAI',
  },
};

export const DAILY_GOAL = 10;

export const STORAGE_KEYS = {
  SETTINGS: 'mathmaster_settings',
  DAILY_PROGRESS: 'mathmaster_daily_progress',
} as const;
